/**
 * World 3 — "Cinder Pass" (Fire). Narrative fragment, merged into the global
 * NODE_STORIES / ZONE_STORIES / WORLD_STORIES maps by campaignConfig.ts.
 *
 * THE TALE (Act I, chapter 3, "The Last Souls"): the mountain's forge-fire is
 * a Drago Guardian ("Cinder") that forgot its fire was ever a GIFT — the
 * shard-feeling is warmth given. Cliff kneels and banks the fire instead of
 * dousing it, warming his hands at it: the saving kindness. Cast beats: Cliff
 * steps past his own gate at the burn-line; Vera (hooded) leaves supplies
 * without being seen; Clan Z (Zip and Zap) caged and drove the Drago, and
 * their creatures (Dash, Shade) return bigger; Chirpy evolves to stage 2 pulling
 * Timmy from a flame-fissure. Old Man Gray never battles — he only warns,
 * in passing.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD3_TAGLINE =
  "The forest is green again and the tide has gone down. Now smoke pours from Cinder Pass. The old forge is cold, but the dragon beside it will not stop burning.";

export const WORLD3_ZONE_STORIES: Record<string, string> = {
  z6: "Smoke fills the pass. Everyone is coming down. One set of footprints goes up.",
  z7: "The smiths have left. Someone is leaving bread and water at the shrines for the stragglers.",
  z8: "Clan Z cages crowd the abandoned forge. Beyond them, Cinder circles the chimney.",
};

export const WORLD3_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 6: Scorch Road (1v1) ---
  "z6-n1": {
    story: "Timmy marches ahead and declares himself your guide. Chirpy looks unconvinced.",
    dialogue: {
      intro: "Official mountain guide, coming through! That's me. First stop: a battle!",
      playerWin: "I lost! Okay. Maybe don't ask me for directions.",
      playerLose: "See? Fully qualified! Chirpy, which way was up?",
      introRematchAfterLoss: "Back again? Good! The mountain guide allows do-overs!",
      introRematchAfterWin: "You again! Chirpy learned a fire-hop. Watch this!",
      playerWinRepeat: "I lost again! Go save the mountain!",
      playerLoseRepeat: "Another win for the mountain guide! No refunds on directions.",
    },
  },
  "z6-n2": {
    story: "Up the road, you spot Mimi perched on the only warm rock.",
    dialogue: {
      intro: "Everyone's heading down. Not us! Bet you can't knock me off my warm rock!",
      playerWin: "You win the warm rock. It's mostly soot anyway.",
      playerLose: "My rock, my victory. Plenty of room if you want to sulk here.",
      introRematchAfterLoss: "Back for my warm rock? I'll try not to fall off it when we battle.",
      introRematchAfterWin: "Aggie says this fire kept folks warm once. Strange, huh?",
      playerWinRepeat: "No fair! You win every bet!",
      playerLoseRepeat: "The rock is warm. You are not!",
    },
  },
  "z6-n3": {
    story: "Heat shimmers ahead of you. A wild Hotpaw has been driven onto the safe path.",
    dialogue: {
      intro: "A wild Hotpaw blocks the safe lane! Its coat smokes!",
      playerWin: "The Hotpaw bounds away up the smoke.",
      playerLose: "The Hotpaw drives you back down the road.",
      introRematchAfterLoss: "The wild Hotpaw paces the lane again. Ready?",
      introRematchAfterWin: "The Hotpaw remembers your squad. It bares its teeth.",
      playerWinRepeat: "The Hotpaw flees the lane. The way up opens.",
      playerLoseRepeat: "The Hotpaw holds the lane. The heat presses in.",
    },
  },
  "z6-n4": {
    story: "At the burn-line, Cliff steps through his own gate toward you.",
    dialogue: {
      intro: "I'm coming past the gate this time. Sentry too. Prove we're not walking into a mistake.",
      playerWin: "Good. Sentry, we're going with them. Yes, past the gate.",
      playerLose: "Another drill, then. I want us all coming back in one piece.",
      introRematchAfterLoss: "The gate is still here. So am I.",
      introRematchAfterWin: "Aggie's up in the smoke. Before you go, Gatefire wants another round.",
      playerWinRepeat: "Beat again! Sentry likes you now.",
      playerLoseRepeat: "Gates are for holding. Not for hiding behind.",
    },
  },

  // --- Zone 7: Ember Woods (2v2) ---
  "z7-n1": {
    story: "The trail ahead of you is burned black. Zip blocks it, and Dash's coat is smoking.",
    dialogue: {
      intro: "Dash evolved! His new coat smokes when he's excited. He's very excited. Keep this Keeper off the cages!",
      playerIntro: "Whoa, Dash! Don't set yourself on fire.",
      playerWin: "That's it. I'm asking Zeck for an easier mountain.",
      playerLose: "Ha! Clan Z still beats you!",
      introRematchAfterLoss: "Back on my trail? Dash is stronger now.",
      introRematchAfterWin: "Dash and I had a team meeting. No, you weren't invited. Battle!",
      playerWinRepeat: "Fine! Keep the smoky pass!",
      playerLoseRepeat: "Too slow! Clan Z runs this mountain!",
    },
  },
  "z7-n2": {
    story: "At a roadside shrine, you catch Vera setting down bread. She freezes under her hood.",
    dialogue: {
      intro: "It's for anyone coming down the pass. Since you're going up, show me your team first.",
      playerWin: "You'll do. Take some water. The fire up there is scared. Don't corner it.",
      playerLose: "You need a rest. Sit down. The bread's still warm.",
      introRematchAfterLoss: "You're back. There's still water if you need it before we start.",
      introRematchAfterWin: "The spirits say the fire is not angry. It is scared.",
      playerWinRepeat: "Scared fires burn hot. Be gentle up there.",
      playerLoseRepeat: "You're exhausted. Sit by the shrine. I'll keep watch.",
    },
  },
  "z7-n3": {
    story: "Behind you, a ledge crumbles! You turn in time to see Chirpy catch Timmy on new wings.",
    dialogue: {
      intro: "Chirpy caught me! You were way up ahead again. Look at those wings! Battle somewhere flatter?",
      playerWin: "You win. Chirpy still gets the medal for catching an entire Timmy.",
      playerLose: "A rescue AND a win! I'm giving her my best biscuit.",
      introRematchAfterLoss: "Chirpy's still proud of that catch. Me too!",
      introRematchAfterWin: "Chirpy won't stop preening. Best bird ever!",
      playerWinRepeat: "Lost again! Chirpy's still the real hero.",
      playerLoseRepeat: "Chirpy wins again! That's nine wins! Or four. Extra biscuits!",
    },
  },
  "z7-n4": {
    story: "At the edge of the woods, you find Aggie warming her hands, smiling up at the old forge.",
    dialogue: {
      intro: "This fire once warmed the whole pass, child. Beat my team and I'll tell you its story.",
      playerWin: "Its name is Cinder, the big red Drago. A Keeper once looked after its fire. Now it thinks everyone comes to put it out.",
      playerLose: "Too rushed, child. Take a breath. There's time for another try.",
      introRematchAfterLoss: "Back again? Good. I'll go a little slower this time.",
      introRematchAfterWin: "That fire was a gift once. A Keeper tended it like a hearth.",
      playerWinRepeat: "Don't put Cinder's fire out. Help it warm someone again.",
      playerLoseRepeat: "Caught you again. Watch the whole team next time, child.",
    },
  },

  // --- Zone 8: Forge Heart (3v3) ---
  "z8-n1": {
    story: "You hear wings in the rafters. Zap waits by the forge cages, and Shade circles overhead.",
    dialogue: {
      intro: "Zip left you for me. Again. Shade evolved. His wings reach the rafters now.",
      playerIntro: "Up to the rafters? Heads down, team. Your turn, Zap.",
      playerWin: "You've opened them. Fine. I'll tell Zeck myself.",
      playerLose: "Quiet wins. The cages stay shut.",
      introRematchAfterLoss: "Back at the forge. Shade watched. Ready.",
      introRematchAfterWin: "Shade knows your moves now.",
      playerWinRepeat: "...Another cage falls open. Zeck knows.",
      playerLoseRepeat: "Clan Z holds the forge. Go down.",
    },
  },
  "z8-nw1": {
    story: "Something stalks between the Clan Z cages, watching you.",
    dialogue: {
      intro: "A wild Drago springs from the coals! It blocks the way!",
      playerWin: "The wild Drago darts back into the smoke.",
      playerLose: "The wild Drago drives you away from the forge.",
      introRematchAfterLoss: "The coals stir. The wild Drago returns.",
      introRematchAfterWin: "The wild Drago knows your squad now. It snarls.",
      playerWinRepeat: "The wild Drago flees the forge.",
      playerLoseRepeat: "The forge stays wild. The heat presses in.",
    },
  },
  "z8-n2": {
    story: "Sparks fall around you. Lulu is already ahead, checking the path through them.",
    dialogue: {
      intro: "Cliff wants to sit beside that fire. I'll get him there. You get me there.",
      playerWin: "You're ready. I'll clear the path. Stay close to Cliff.",
      playerLose: "Not yet. The fire won't give you that much room.",
      introRematchAfterLoss: "Back from the heat? Have some water. Then we try again.",
      introRematchAfterWin: "Cliff wants to warm his hands by it. I think he's onto something.",
      playerWinRepeat: "Gray's tracks don't stop at the forge. They go on up.",
      playerLoseRepeat: "The forge is mine. Rest first.",
    },
  },
  "z8-n3": {
    story: "Mimi shows you exactly where she ran from Cinder.",
    dialogue: {
      intro: "I got as far as 'nice dragon.' Then I ran. Beat me and I'll try again with you.",
      playerWin: "All right. I'm coming. You can do the 'nice dragon' part.",
      playerLose: "We both need more practice. Preferably down here.",
      introRematchAfterLoss: "Back for another try? I like that.",
      introRematchAfterWin: "I'm still nervous up here. A battle with you might help.",
      playerWinRepeat: "Still braver than me. That's okay.",
      playerLoseRepeat: "Ha! I won! Now my story has a happy part.",
    },
  },
  "z8-n4": {
    story: "Cinder the Drago circles the cold forge, roaring. Beside you, Cliff takes off his gloves.",
    dialogue: {
      intro: "Cinder blocks the forge. You'll have to face it before Cliff can tend the fire.",
      playerWin: "Cliff calms the flames and warms his hands. Cinder sits beside him and gives you a warm shard. That's eight! The fire feels like a gift again.",
      playerLose: "The fire drives you down the pass.",
      introRematchAfterLoss: "Cinder flares again. Cliff waits nearby, gloves in hand.",
      introRematchAfterWin: "Cinder keeps its coals warm for you now. The only happy fire on this mountain.",
      playerWinRepeat: "Cliff warms his hands again. The forge glows brighter for the visit.",
      playerLoseRepeat: "The flame flickers but does not die. It waits for you.",
    },
  },
};
